import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosHeaders } from 'axios';
import { API } from 'network/api';
import { useCallback, useMemo, useState } from 'react';
import { PaymentRecord } from 'screens/collections/hotcash/types';

const fetchAllPendingInvoices = async (
  params?: Record<string, string | number>,
) => {
  try {
    const allParams = {
      'status.equals': 'PENDING',
      page: 0,
      size: 10,
      sort: 'paidAt,desc',
      'paymentType.equals': 'banktransfer',
      'expiredAt.greaterThan': new Date().toISOString(),
      projection: 'collection-payment-season-list-dto',
      ...params,
    };
    const res = await API.get('/collection', {
      params: allParams,
    });

    return {
      data: res?.data,
      headers: res?.headers,
    };
  } catch (error) {
    if (error instanceof Error) throw new Error(error?.message);
    throw new Error('Something went wrong');
  }
};

const usePendingInvoiceListQuery = (
  params?: Record<string, string | number>,
) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const queryClient = useQueryClient();

  const allParams = useMemo(
    () => ({ ...params, page: currentPage - 1, size: pageSize }),
    [params, currentPage, pageSize],
  );

  const {
    data: response,
    isLoading,
    isRefetching: isFetching,
    error,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['pendingInvoiceList', allParams],
    queryFn: () => fetchAllPendingInvoices(allParams),
    staleTime: 1000 * 10,
  });

  const handlePageChange = useCallback((page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const headers = response?.headers as AxiosHeaders;
  const data = response?.data;
  const total = Number(headers?.get('X-Total-Count')) || 0;

  const updateInvoiceAmount = useCallback(
    (id: string, amount: number) => {
      console.log(id, amount);
      queryClient.setQueryData(
        ['pendingInvoiceList', allParams],
        (oldData: { data: PaymentRecord[] }) => ({
          ...oldData,
          data: oldData?.data?.map((item: PaymentRecord) => {
            if (item?.invoiceNumber === id) {
              return {
                ...item,
                amount,
              };
            }
            return item;
          }),
        }),
      );
    },
    [queryClient, allParams],
  );

  return {
    isLoading,
    isError,
    isFetching,
    error,
    data,
    total,
    currentPage,
    pageSize,
    handlePageChange,
    refetch,
    updateInvoiceAmount,
  };
};

export default usePendingInvoiceListQuery;
